<template>
  <CCard class="mb-3">
    <CCardHeader class="d-flex align-items-center justify-content-between">
      <!-- Left: Micro Table with Order Data -->
      <table class="order-info-table">
        <tbody>
          <tr v-if="hasPerm('edge.print.transaction_number')">
            <td class="label">
              <translate translate-context="edge-prints.summary">Order</translate>
            </td>
            <td class="value">{{ order.transaction_number }}</td>
          </tr>
          <tr v-if="hasPerm('edge.print.cabin')">
            <td class="label">
              <translate translate-context="edge-prints.summary">Cabin</translate>
            </td>
            <td class="value">{{ order.cabin_id }}</td>
          </tr>
          <tr v-if="hasPerm('edge.print.guest')">
            <td class="label">
              <translate translate-context="edge-prints.summary">Guest</translate>
            </td>
            <td class="value">{{ order.guest_id }}</td>
          </tr>
          <tr v-if="order.picacodes && order.picacodes.length > 0">
            <td class="label">
              <translate translate-context="edge-prints.summary">PicaCode</translate>
            </td>
            <td class="value">
              <template v-for="(code, index) in order.picacodes">
                <router-link
                  v-if="canBrowsePhoto"
                  :key="index"
                  :to="{
                    name: 'photoSearch.search',
                    params: { picaServiceId: picaServiceId },
                    query: { pica_code: code }
                  }"
                  v-c-tooltip="{content: $pgettext('edge-prints.summary', 'Browse Photos')}"
                  class="picacode-link"
                >
                  {{ code }}
                </router-link>
                <span v-else :key="index">{{ code }}</span>
                <span v-if="index < order.picacodes.length - 1" :key="'sep-' + index">, </span>
              </template>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Right: Status Badges + Action Buttons -->
      <div class="d-flex align-items-center">
        <!-- Print Status Indicator -->
        <div v-if="totalMediaCounts.total > 0" class="mr-2">
          <div v-if="allPrintableItemsCompleted" class="status-badge status-completed">
            <CIcon name="cilCheckCircle" size="lg" class="mr-2"/>
            <div>
              <strong><translate translate-context="edge-prints.summary">All Photos Printed</translate></strong>
              <small class="d-block">
                <translate translate-context="edge-prints.summary"
                          :translate-params="{total: totalMediaCounts.total}">
                  %{total} photos completed
                </translate>
              </small>
            </div>
          </div>
          <div
            v-else
            class="status-badge status-pending clickable"
            @click="selectToPrint"
            role="button"
            :title="$pgettext('edge-prints.summary', 'Click to select photos to print')"
          >
            <CIcon name="cilPrint" size="lg" class="mr-2"/>
            <div>
              <strong>
                <translate translate-context="edge-prints.summary"
                          :translate-params="{items: itemsToPrint}"
                          :translate-n="itemsToPrint"
                          translate-plural="%{items} items to print">
                  %{items} item to print
                </translate>
              </strong>
              <small class="d-block">
                <translate translate-context="edge-prints.summary"
                          :translate-params="{photos: uniquePhotosToPrint}"
                          :translate-n="uniquePhotosToPrint"
                          translate-plural="relative to %{photos} photos">
                  relative to %{photos} photo
                </translate>
              </small>
            </div>
            <CIcon name="cilChevronRight" class="ml-auto"/>
          </div>
        </div>

        <!-- Print Jobs Summary -->
        <div class="mr-2">
          <div
            class="status-badge status-jobs"
            :class="{ 'clickable': printJobs.length > 0 }"
            @click="printJobs.length > 0 && scrollToPrintJobs()"
            :role="printJobs.length > 0 ? 'button' : null"
            :title="printJobs.length > 0 ? $pgettext('edge-prints.summary', 'Click to view print jobs') : null"
          >
            <CIcon name="cilListNumbered" size="lg" class="mr-2"/>
            <div>
              <strong v-if="printJobs.length > 0">
                {{ printJobs.length }}
                <translate translate-context="edge-prints.summary"
                          :translate-n="printJobs.length"
                          translate-plural="Print Jobs">
                  Print Job
                </translate>
              </strong>
              <strong v-else>
                <translate translate-context="edge-prints.summary">0 Print Jobs</translate>
              </strong>
              <small class="d-block">
                <translate v-if="printJobs.length > 0" translate-context="edge-prints.summary">
                  Show print jobs
                </translate>
                <translate v-else translate-context="edge-prints.summary">
                  Ready to print
                </translate>
              </small>
            </div>
            <CIcon v-if="printJobs.length > 0" name="cilChevronRight" class="ml-auto"/>
          </div>
        </div>

        <!-- Select All / Deselect All Button -->
        <div
          v-if="totalMediaCounts.total > 0"
          class="status-badge status-action status-action-secondary clickable mr-2"
          @click="!loading && toggleSelectAll()"
          role="button"
          :title="allSelected ? $pgettext('edge-prints.summary', 'Click to deselect all') : $pgettext('edge-prints.summary', 'Click to select all')"
        >
          <CSpinner v-if="loading" size="sm" class="mr-2"/>
          <CIcon v-else :name="allSelected ? 'cipClose' : 'cipCheckbox'" size="lg" class="mr-2"/>
          <div>
            <strong>
              <translate v-if="!allSelected" translate-context="edge-prints.summary">
                Select All
              </translate>
              <translate v-else translate-context="edge-prints.summary">
                Deselect All
              </translate>
            </strong>
            <small class="d-block">
              <translate translate-context="edge-prints.summary"
                        :translate-params="{count: totalMediaCounts.total}"
                        :translate-n="totalMediaCounts.total"
                        translate-plural="%{count} photos">
                %{count} photo
              </translate>
            </small>
          </div>
        </div>

        <!-- Mark Order as Printed Badge -->
        <div
          v-if="!isCompletedOrder && hasPerm('edge.print.update')"
          class="status-badge status-action clickable"
          :class="allPrintableItemsCompleted ? 'status-action-success' : 'status-action-secondary'"
          @click="!loading && $emit('mark-order-as-printed')"
          role="button"
          :title="$pgettext('edge-prints.summary', 'Click to mark order as printed')"
        >
          <CSpinner v-if="loading" size="sm" class="mr-2"/>
          <CIcon v-else name="cilCheckCircle" size="lg" class="mr-2"/>
          <div>
            <strong>
              <translate translate-context="edge-prints.summary">Mark as Printed</translate>
            </strong>
            <small v-if="allPrintableItemsCompleted" class="d-block">
              <translate translate-context="edge-prints.summary">All photos complete</translate>
            </small>
            <small v-else class="d-block">
              <translate translate-context="edge-prints.summary">Finalize this order</translate>
            </small>
          </div>
        </div>

        <!-- Mark to Process Badge (for completed orders) -->
        <div
          v-if="isCompletedOrder && hasPerm('edge.print.update')"
          class="status-badge status-action status-action-secondary clickable"
          @click="!loading && $emit('mark-order-as-to-process')"
          role="button"
          :title="$pgettext('edge-prints.summary', 'Click to reopen order')"
        >
          <CSpinner v-if="loading" size="sm" class="mr-2"/>
          <CIcon v-else name="cilReload" size="lg" class="mr-2"/>
          <div>
            <strong>
              <translate translate-context="edge-prints.summary">Reopen Order</translate>
            </strong>
            <small class="d-block">
              <translate translate-context="edge-prints.summary">Mark to process</translate>
            </small>
          </div>
        </div>
      </div>
    </CCardHeader>
  </CCard>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { getOrderStatusLabel, getOrderStatusColor } from '@/domain/core/constant/orderStatus'
import Filters from '@/domain/core/utils/filters'

export default {
  name: 'OrderSummary',
  props: {
    order: {
      type: Object,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
    }
  },
  computed: {
    ...mapGetters('edgePrints', [
      'isProcessingOrder',
      'isCompletedOrder',
      'getTotalMediaCounts',
      'getTotalSelectedCount',
      'isAllMediaPrefetched',
      'getUnprintedMediaIdsForOrderItem',
      'orderPrintJobs',
      'picaServiceId'
    ]),
    ...mapGetters('eventWizard', [
      'canSeePhotoSearch'
    ]),

    totalMediaCounts() {
      return this.getTotalMediaCounts
    },

    allPrintableItemsCompleted() {
      return this.totalMediaCounts.total > 0 && this.unprintedMediaCount === 0
    },

    printJobs() {
      return this.orderPrintJobs || []
    },

    pendingJobsCount() {
      // Status 0 = pending
      return this.printJobs.filter(job => job.status === 0).length
    },

    completedJobsCount() {
      // Status 1 = completed
      return this.printJobs.filter(job => job.status === 1).length
    },

    statusLabel() {
      return getOrderStatusLabel(this.order.status)
    },

    statusColor() {
      return getOrderStatusColor(this.order.status)
    },

    statusBadgeColor() {
      const color = this.statusColor
      const badgeColorMap = {
        'primary': 'light',
        'success': 'light',
        'warning': 'light',
        'danger': 'light',
        'secondary': 'light',
        'dark': 'light'
      }
      return badgeColorMap[color] || 'light'
    },

    allSelected() {
      return this.getTotalSelectedCount === this.totalMediaCounts.total && this.totalMediaCounts.total > 0
    },

    unprintedMediaCount() {
      // Use the total media counts getter which tracks remaining photos
      return this.totalMediaCounts.remaining || 0
    },

    itemsToPrint() {
      // Total number of copies to print across all order items
      let totalItems = 0
      this.orderItems.forEach(item => {
        if (item.is_printable) {
          const mediaData = this.$store.state.edgePrints.orderItemsMedia[item.id]
          if (mediaData?.loaded) {
            const requiredCopies = mediaData.requiredCopies || 1
            const printedCopies = mediaData.printedCopies || {}

            // Count how many photos need more copies
            mediaData.allMediaIds.forEach(mediaId => {
              const copiesPrinted = printedCopies[mediaId] || 0
              const copiesNeeded = Math.max(0, requiredCopies - copiesPrinted)
              totalItems += copiesNeeded
            })
          }
        }
      })
      return totalItems
    },

    uniquePhotosToPrint() {
      // Number of unique photos that still need printing
      return this.unprintedMediaCount
    },

    orderItems() {
      return this.$store.state.edgePrints.orderItems || []
    },

    canBrowsePhoto() {
      return this.canSeePhotoSearch && this.hasPerm('photo_search.filter_by_pica_code')
    }
  },
  methods: {
    ...mapActions('edgePrints', [
      'clearAllSelections',
      'selectAllMediaForOrderItem'
    ]),

    toggleSelectAll() {
      if (this.allSelected) {
        this.clearAllSelections()
      } else {
        // Select all media across all order items (both printable and digital)
        this.orderItems.forEach(item => {
          // Check if item has media (printable or digital)
          const hasMedia = item.is_printable ||
            (item.media_ids && item.media_ids.length > 0) ||
            (item.gallery_ids && item.gallery_ids.length > 0)

          if (hasMedia) {
            this.selectAllMediaForOrderItem(item.id)
          }
        })
      }
    },

    selectToPrint() {
      // Select only media that doesn't have all required copies printed
      const groupedItems = []

      this.orderItems.forEach(item => {
        if (item.is_printable) {
          const unprintedIds = this.getUnprintedMediaIdsForOrderItem(item.id)

          if (unprintedIds.length > 0) {
            // Select the media
            this.$emit('bulk-select', {
              orderItemId: item.id,
              selectedIds: unprintedIds
            })

            // Add to grouped items for print modal
            groupedItems.push({
              orderItem: item,
              order: this.order,
              mediaIds: unprintedIds
            })
          }
        }
      })

      // Open print summary modal with selected items
      if (groupedItems.length > 0) {
        this.$emit('open-print-summary', groupedItems)
      }
    },

    scrollToPrintJobs() {
      // Scroll to print jobs section
      this.$nextTick(() => {
        const printJobsSection = document.querySelector('.print-jobs-container')
        if (printJobsSection) {
          printJobsSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      })
    }
  },
  filters: {
    currency: Filters.currency,
    formatDate: Filters.formatDate
  }
}
</script>

<style scoped>
/* Micro table for order info */
.order-info-table {
  font-size: 0.875rem;
  border-collapse: collapse;
  margin-right: 2rem;
}

.order-info-table td {
  padding: 0.25rem 0.5rem;
  vertical-align: top;
}

.order-info-table td.label {
  color: #6c757d;
  font-weight: 500;
  padding-right: 0.75rem;
  white-space: nowrap;
}

.order-info-table td.value {
  color: #2c3e50;
  font-weight: 600;
  font-size: 0.95rem;
}

/* Status badges */
.status-badge {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  border: 2px solid;
  min-width: 200px;
  transition: all 0.2s;
}

.status-badge.clickable {
  cursor: pointer;
}

.status-completed {
  background: #d4edda;
  border-color: #28a745;
  color: #155724;
}

.status-pending {
  background: #fff3cd;
  border-color: #ffc107;
  color: #856404;
}

.status-pending.clickable:hover {
  background: #ffe69c;
  border-color: #ffb300;
}

.status-jobs {
  background: #e7f3ff;
  border-color: #007bff;
  color: #004085;
}

.status-jobs.clickable:hover {
  background: #cce5ff;
  border-color: #0056b3;
}

.status-badge strong {
  font-size: 0.95rem;
}

.status-badge small {
  font-size: 0.8rem;
  opacity: 0.85;
  margin-top: 0.25rem;
}

/* PicaCode link */
.picacode-link {
  color: #321fdb;
  font-weight: 600;
  text-decoration: none;
}

.picacode-link:hover {
  color: #2819a0;
  text-decoration: underline;
}

/* Action badges (Mark as Printed, etc) */
.status-action-success {
  background: #d4edda;
  border-color: #28a745;
  color: #155724;
}

.status-action-success:hover {
  background: #c3e6cb;
  border-color: #1e7e34;
}

.status-action-secondary {
  background: #e2e3e5;
  border-color: #6c757d;
  color: #383d41;
}

.status-action-secondary:hover {
  background: #d6d8db;
  border-color: #545b62;
}
</style>